import { useState } from 'react';
import { CEW_SITE_URL, CEW_LINK_TARGET, CEW_SEARCH_URL } from '../../lib/cew-site';
import { CewSiteNav } from './CewSiteNav';
import { MobileMenuDrawer } from './MobileMenuDrawer';

/**
 * Sticky navy header — CEW chrome parity (CR-028/029, 2026-08-17).
 * Replicates cew.georgetown.edu's own header so a visitor moving
 * between the CEW site and this tool (embedded as a full-page iframe
 * on a blank WordPress page, DEC-029) sees no navigational break.
 *
 * Bar height: ONE owner, the responsive CSS custom property
 * `--h-topbar` (68px base / 74px at >=1001px — see `globals.css` +
 * `lib/layout-offsets.ts`). `h-[var(--h-topbar)]` is the header's only
 * height class (2026-08-18, Frank staging review, DEC-075 two-dial
 * cleanup — a prior mobile-height base class + `min-[1001px]:h-…`
 * override raced for the same outcome; the CSS var already carries
 * the responsiveness, so the override was redundant).
 *
 * The desktop NAV CLUSTER (the `CewSiteNav` component — Research /
 * Resource Library / Press Room / Blog / About CEW / Subscribe pill /
 * search, right-aligned) has its OWN, higher floor: >=1136px -- the
 * width at which this cluster's row fits on a single line with no
 * wrapping (2026-08-18, tech-lead decision post Frank-staging-review
 * fix loop -- see the desktop cluster's own doc note below for the
 * measurement history). 1001-1135px is therefore a deliberate hybrid
 * band: the CEW-matched bar (geometry above) with the MOBILE hamburger
 * cluster inside it, not a garbled desktop row and not CEW's own
 * <=1000px mobile chrome either.
 *
 * Mobile cluster (<=1135px): logo left, a search icon + 40px hamburger
 * circle on the right. The hamburger opens `MobileMenuDrawer`, which
 * carries both the tool's 6 page tabs AND a replica "cew.georgetown.edu"
 * nav group (plus the Report link, see below) since the desktop
 * CewSiteNav is hidden below 1136px. The desktop nav is shown ONLY
 * where it fits on one line -- the drawer serves every narrower width,
 * including the 1001-1135px hybrid band.
 *
 * The logo links to `CEW_SITE_URL` with `target={CEW_LINK_TARGET}`
 * ("_top") — Frank asked for "the same window"; `_top` (not "_self")
 * is required because the tool lives inside an iframe, so a same-
 * window link would otherwise load cew.georgetown.edu *inside* the
 * iframe instead of replacing the page.
 *
 * Report-link history: added 2026-07-02 (V2 feedback) as a
 * persistent report link; shipped as a dead `href="#"` (code-review
 * finding #2, 2026-07-06), reworked to a `disabled` <button>, then
 * CR-024 (UIS review, 2026-07-31) made it an active link inside this
 * header's desktop cluster. CR-027/028/029/030 (Frank staging review,
 * 2026-08-18, tech-lead decision): REMOVED from this header entirely
 * and re-homed to `SectionNav`'s right edge -- the CEW replica row has
 * no room for a tool-only item at 1136-1280px, and this header must
 * stay a pure cew.georgetown.edu replica. The mobile drawer's copy
 * (`DrawerDownloadLinks`) carries it below the floor; CR-034 (2026-08-28)
 * made it the "Download Full Report" PDF button.
 */
export function Topbar() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <>
      <header
        // z-[360] — "Topbar / site header" tier. Sits ABOVE SectionNav
        // (z-[350]) but below RowPeekDrawer (z-[400]): the CEW nav's
        // submenu panels render inside this header, so the header must
        // win the stacking order once SectionNav goes sticky on scroll
        // (2026-08-18, Frank staging review — the old z-40 let
        // SectionNav visually obscure the open submenu panels).
        //
        // gap-x-6 (2026-08-18, visual-verify finding): the desktop nav
        // cluster is right-aligned via `ml-auto`, which only creates
        // visible space next to the logo out of genuinely LEFTOVER
        // width. `gap-x-6` (24px) is honored by the flex layout as a
        // guaranteed floor next to the logo regardless of how much
        // width the nav cluster itself consumes, so the nav can never
        // touch the logo the way an unguarded `ml-auto` alone would
        // allow — matching CEW's own site, which always keeps a clear
        // gutter next to the logo. (Superseded 2026-08-18, tech-lead
        // decision: the desktop nav cluster is now shown ONLY at
        // widths where its own row already fits on one line — see the
        // desktop cluster's own doc note below — so the original
        // wrapping/shrink scenario this comment described no longer
        // occurs; the gap-x-6 floor is retained regardless as cheap
        // insurance.)
        className="sticky top-0 z-[360] flex h-[var(--h-topbar)] items-center gap-x-6 bg-navy px-7 shadow-topbar min-[1001px]:px-[6vw]"
        role="banner"
      >
        <a
          href={CEW_SITE_URL}
          target={CEW_LINK_TARGET}
          rel="noopener"
          aria-label="CEW Georgetown — cew.georgetown.edu home"
          className="flex shrink-0 items-center no-underline"
        >
          <img
            src="/assets/cew-logo-white.png"
            alt="Georgetown University Center on Education and the Workforce"
            className="h-6 w-auto min-[1001px]:h-[38px]"
            onError={(e) => {
              // If the PNG fails to load, fall back to the wordmark text.
              const fallback = document.createElement('span');
              fallback.className = 'flex flex-col gap-px leading-none';
              fallback.innerHTML =
                '<span class="text-[13px] font-bold text-white">Missed Opportunities</span><span class="text-[9px] font-semibold uppercase tracking-widest text-white/55">Georgetown CEW</span>';
              (e.currentTarget as HTMLImageElement).replaceWith(fallback);
            }}
          />
        </a>

        {/* Desktop cluster: the full CEW site nav alone (the Report link
            moved to SectionNav, CR-027/028/029/030). REVERSED 2026-08-18
            (tech-lead decision, post Frank-staging-review fix loop):
            CEW's own breakpoint is 1001px, but our nav's min-content
            (its 6 items + Subscribe + search) genuinely doesn't fit that
            narrow. An earlier pass let items line-wrap below a raised
            floor and corrected the resulting "orphaned chevron" case
            with a ResizeObserver + document.fonts + transform
            measurement effect in CewSiteNav -- that machinery pushed
            CewSiteNav past the CLAUDE.md 250-line file cap and was
            disproportionate for a ~110px viewport band. Superseded:
            the desktop nav is now shown ONLY where its full row fits
            on a SINGLE line (Playwright-measured: >=1136px is the
            smallest Tailwind-native breakpoint at/above the width
            where every item renders single-line with no overflow).
            1001-1135px is therefore a deliberate hybrid band -- the
            CEW-matched bar (geometry unchanged, see file doc note
            above) but showing the MOBILE hamburger cluster instead of
            a garbled or wrapping desktop row. */}
        {/* h-full completes the height chain (header -> this div -> nav
            -> ul -> li) so CewSiteNav's submenu panel `top-[calc(100%-9px)]`
            resolves against the bar's real bottom edge (2026-08-18
            follow-up; see CewSiteNav's own h-full doc note). */}
        <div className="ml-auto hidden h-full items-center min-[1136px]:flex">
          <CewSiteNav />
        </div>

        {/* Mobile cluster (<=1135px): search icon + hamburger. CewSiteNav's
            full menu doesn't fit single-line below 1136px (see the
            desktop cluster's doc note above); MobileMenuDrawer carries
            the CEW nav group + Report link instead. Hidden floor
            matches the desktop cluster's visibility floor
            (min-[1136px]) so exactly one of the two clusters is ever
            showing -- never both, never neither. This covers the
            1001-1135px hybrid band too: the bar/logo geometry above
            already switched to CEW's desktop chrome there, but the nav
            cluster itself doesn't fit yet, so the mobile hamburger
            cluster is the correct (and only) affordance shown. */}
        <div className="ml-auto flex items-center gap-3 min-[1136px]:hidden">
          <a
            href={CEW_SEARCH_URL}
            target={CEW_LINK_TARGET}
            rel="noopener"
            aria-label="Search cew.georgetown.edu"
            className="flex h-11 w-11 items-center justify-center text-white"
          >
            <svg width={22} height={22} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} aria-hidden="true">
              <circle cx="11" cy="11" r="7" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
          </a>

          <button
            type="button"
            onClick={() => setIsMenuOpen(true)}
            aria-label="Open menu"
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu-drawer"
            className="flex h-11 w-11 items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-white/40"
          >
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-dark text-white transition hover:opacity-90">
              <svg
                width={20}
                height={20}
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth={2}
                strokeLinecap="round"
                strokeLinejoin="round"
                aria-hidden="true"
              >
                <line x1="3" y1="6" x2="21" y2="6" />
                <line x1="3" y1="12" x2="21" y2="12" />
                <line x1="3" y1="18" x2="21" y2="18" />
              </svg>
            </span>
          </button>
        </div>
      </header>

      <MobileMenuDrawer isOpen={isMenuOpen} onClose={() => setIsMenuOpen(false)} />
    </>
  );
}
